import { v4 as uuid } from 'uuid';
import { ImportResultsRecord } from '@components/bid_request/details/types';

import { LineItem } from 'types';
import { formatStringAsNumber } from './formatStringAsNumber';

const TRUTHY_IMPORT_VALUES = ['1', 'true', 'yes'];

const parseImportBoolean = (value: boolean | string): boolean =>
  TRUTHY_IMPORT_VALUES.includes(String(value).trim().toLowerCase());

/**
 * @param {ImportResultsRecord} row Data row.
 * @param {Boolean} isPriceUpdate Whether the event is a price update. A price update is not a
 *   negotiation - the only meaningful values are the previously-agreed price and the now-agreed
 *   price - so it carries neither a negotiation_starting_price nor the buyer-provided baseline
 *   marker. Every other event type gets both.
 * @param {Boolean} acceptSuggestedPriceColumn Whether the event accepts a mapped Suggested Price
 *   column (see shouldAcceptSuggestedPriceImportColumn). When false the column's values are
 *   discarded even if the sheet carried them.
 * @returns {LineItem}
 */
export const createLineItem = (
  row: ImportResultsRecord,
  isPriceUpdate = false,
  acceptSuggestedPriceColumn = false,
): Partial<LineItem> => ({
  extended_quantity: row.quantity_of_items
    ? formatStringAsNumber((row.quantity_of_items as string) || '0.00')
    : formatStringAsNumber(row.quantity_of_units || '0.00'),
  id: `new-${uuid()}`,
  // Blank leaves the key out so the row keeps the backend default (not required).
  ...(row.is_required !== undefined && row.is_required !== '' && {
    is_required: parseImportBoolean(row.is_required),
  }),
  name: row.line_item_name,
  // Always include negotiation_starting_price off a price update.
  // Falls back to price_per_unit to support old templates that don't contain the negotiation_starting_price column.
  // Treat empty string as absent (OneSchema sends '' for blank cells) so the backfill triggers.
  ...(!isPriceUpdate && {
    negotiation_starting_price: formatStringAsNumber(
      (row.negotiation_starting_price !== '' ? row.negotiation_starting_price : undefined)
        ?? row.price_per_unit ?? '0.00'
    ),
  }),
  // User-provided baseline (NSB #3/#7): when the upload carries a real price-per-unit (> 0), mark the
  // row is_buyer_provided_baseline_price. This is a WRITE-ONCE flag stamped on the row at import; the NSB seeder
  // reads it and skips baselining the row (keeping the user's price), and it can never be flipped
  // afterward (the backend excludes it from the update-columns list). Blank/0 → left unset → existing flow.
  ...(!isPriceUpdate && formatStringAsNumber(row.price_per_unit || '0.00') > 0 && {
    is_buyer_provided_baseline_price: true,
  }),
  price: formatStringAsNumber(row.price_per_unit || '0.00'),
  quantity: formatStringAsNumber((row.items_per_unit as string) || '1.00'),
  // Suggested Price maps to the LineItem's `suggested_price_override` column. A blank cell
  // (OneSchema sends '' for an empty cell) is the buyer saying "let AI pricing suggest it": the
  // key is omitted entirely so the override persists as null and the predictive price owns the
  // row. Any present value — including an explicit 0 — is a deliberate buyer override.
  //
  // Note the absence of the `|| '0.00'` fallback the fields above use: that fallback is exactly
  // what would collapse a blank cell into a $0 override and silently pin every unfilled row.
  // An all-blank column therefore needs no special case — every row just omits the key.
  ...(acceptSuggestedPriceColumn && row.suggested_price !== undefined && row.suggested_price !== '' && {
    suggested_price_override: formatStringAsNumber(row.suggested_price as string),
  }),
  units: (row.unit_of_measure || '').trim()
});
